import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import NotificationCenter from '../components/NotificationCenter';
import UserManagement from '../components/admin/UserManagement';
import SessionManagement from '../components/admin/SessionManagement';
import SponsorManagement from '../components/admin/SponsorManagement';
import NewsManagement from '../components/admin/NewsManagement';
import SettingsManagement from '../components/admin/SettingsManagement';
import { API_BASE_URL } from '../config';
import { User, UserSpeaker, Session, Speaker, Sponsor, NewsItem, CfpSubmission } from '../types/conference';
import { formatImageUrl } from '../utils/imageUtils';
import SpeakerManagement from '../components/admin/SpeakerManagement';

// Helper function to map UserSpeaker to Speaker
const mapUserSpeakerToSpeaker = (userSpeaker: UserSpeaker): Speaker => {
  const finalId = userSpeaker.speaker_id ? String(userSpeaker.speaker_id) : userSpeaker.id;
  console.log('Mapping UserSpeaker:', { id: userSpeaker.id, speaker_id: userSpeaker.speaker_id, finalId, name: userSpeaker.name });
  return {
    id: finalId,
    name: userSpeaker.name,
    email: userSpeaker.email,
    bio: userSpeaker.bio || 'No bio available',
    company: userSpeaker.company || 'N/A',
    role: userSpeaker.title || 'Speaker',
    title: userSpeaker.title,
    image: userSpeaker.image_url,
    photoUrl: formatImageUrl(userSpeaker.image_url),
    topics: [], // Default empty array since UserSpeaker doesn't have topics
    linkedin_url: userSpeaker.linkedin_url,
    display_on_public: userSpeaker.display_on_public,
    bio_short: userSpeaker.bio_short,
    talk_title: userSpeaker.talk_title,
    talk_abstract: userSpeaker.talk_abstract,
  };
};

// Helper function to map UserSpeaker[] to Speaker[]
const mapUserSpeakersToSpeakers = (userSpeakers: UserSpeaker[]): Speaker[] => 
  userSpeakers.map(mapUserSpeakerToSpeaker);

// Helper to normalize a session returned by the API where speaker is a UserSpeaker
const normalizeSessionFromApi = (apiSession: any): Session => ({
  ...apiSession,
  speaker: apiSession?.speaker ? mapUserSpeakerToSpeaker(apiSession.speaker as UserSpeaker) : undefined,
});

interface Analytics {
  totalRegistrations: number;
  totalSessions: number;
}

type ActiveTab = 'users' | 'speakers' | 'sessions' | 'sponsors' | 'news' | 'cfp' | 'settings';

const getAdminFetchOptions = (options: RequestInit = {}): RequestInit => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {})
  };
  
  // Add the authorization token if available
  const token = localStorage.getItem('token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return {
    credentials: 'include' as RequestCredentials,
    headers,
    ...options,
  };
};

const AdminDashboard: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [speakers, setSpeakers] = useState<UserSpeaker[]>([]);
  const [adminSpeakers, setAdminSpeakers] = useState<Speaker[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [totalRegistrations, setTotalRegistrations] = useState(0);
  const [totalSessionsCount, setTotalSessionsCount] = useState(0);
  const [activeTab, setActiveTab] = useState<ActiveTab>('users');
  const [submissions, setSubmissions] = useState<CfpSubmission[]>([]);
  const [conferenceSettings, setConferenceSettings] = useState<any>(null);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [news, setNews] = useState<NewsItem[]>([]);
  const [schedulingCfp, setSchedulingCfp] = useState<CfpSubmission | null>(null);

  // Memoized speaker list mapped to Speaker type for child components
  const speakerList = useMemo(() => mapUserSpeakersToSpeakers(speakers), [speakers]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          usersRes,
          speakersRes,
          adminSpeakersRes,
          sessionsRes,
          analyticsRes,
          submissionsRes,
          settingsRes,
          sponsorsRes,
          newsRes,
        ] = await Promise.all([
          fetch(`${API_BASE_URL}/api/admin/users?exclude_speakers=true`, getAdminFetchOptions()),
          fetch(`${API_BASE_URL}/api/admin/speakers`, getAdminFetchOptions()),
          fetch(`${API_BASE_URL}/api/admin/speakers`, getAdminFetchOptions()),
          fetch(`${API_BASE_URL}/api/admin/sessions`, getAdminFetchOptions()),
          fetch(`${API_BASE_URL}/api/admin/analytics`, getAdminFetchOptions()),
          fetch(`${API_BASE_URL}/cfp/submissions`, getAdminFetchOptions()),
          fetch(`${API_BASE_URL}/api/admin/conference-settings`, getAdminFetchOptions()),
          fetch(`${API_BASE_URL}/api/admin/sponsors`, getAdminFetchOptions()),
          fetch(`${API_BASE_URL}/api/admin/news`, getAdminFetchOptions()),
        ]);

        const [
          usersData,
          speakersData,
          adminSpeakersData,
          sessionsData,
          analyticsData,
          submissionsData,
          settingsData,
          sponsorsData,
          newsData,
        ] = await Promise.all([
          usersRes.json(),
          speakersRes.json(),
          adminSpeakersRes.json(),
          sessionsRes.json(),
          analyticsRes.json(),
          submissionsRes.json(),
          settingsRes.json(),
          sponsorsRes.json(),
          newsRes.json(),
        ]);

        // Ensure we're setting arrays
        const usersList = Array.isArray(usersData) ? usersData : (usersData?.users || []);
        const speakersList = Array.isArray(speakersData) ? speakersData : (speakersData?.speakers || []);
        const adminSpeakersList = Array.isArray(adminSpeakersData) ? adminSpeakersData : (adminSpeakersData?.speakers || []);
        const sessionsList = Array.isArray(sessionsData) ? sessionsData : (sessionsData?.sessions || []);
        const submissionsList = Array.isArray(submissionsData) ? submissionsData : (submissionsData?.submissions || []);
        const sponsorsList = Array.isArray(sponsorsData) ? sponsorsData : (sponsorsData?.sponsors || []);
        const newsList = Array.isArray(newsData) ? newsData : (newsData?.news || []);
        setTotalSessionsCount(analyticsData?.totalSessions || 0);
        setSubmissions(Array.isArray(submissionsData) ? (submissionsData as CfpSubmission[]) : []);
        setConferenceSettings(settingsData || null);
        setSponsors(Array.isArray(sponsorsData) ? sponsorsData : (sponsorsData?.sponsors || []));

        setUsers(usersList);
        setSpeakers(speakersList);
        // Map adminSpeakers correctly using speaker_id instead of user id
        const mappedAdminSpeakers = adminSpeakersList.map((userSpeaker: any) => mapUserSpeakerToSpeaker(userSpeaker));
        // Sort by speaker ID for consistent ordering
        mappedAdminSpeakers.sort((a: Speaker, b: Speaker) => parseInt(a.id) - parseInt(b.id));
        setAdminSpeakers(mappedAdminSpeakers);
        // Normalize sessions to ensure speaker shape matches Speaker type
        setSessions((sessionsList as any[]).map(normalizeSessionFromApi));
        setTotalRegistrations(analyticsData?.totalRegistrations || 0);

        // Process news data to match the expected format for NewsManagement component
        const newsItems = newsData?.news || [];
        const formattedNews: NewsItem[] = newsItems.map((item: any): NewsItem => ({
          id: item.id.toString(),
          title: item.title,
          content: item.content || '',
          publishDate: item.published_date || new Date().toISOString().split('T')[0],
          author: item.author || 'Conference Team',
          category: item.category || 'announcement',
          featured: Boolean(item.featured),
          // Ensure strict union for status
          status: item.status === 'draft' ? 'draft' : 'published',
        }));

        setNews(formattedNews);
      } catch (error) {
        console.error('Error fetching admin data:', error);
      }
    };

    fetchData();
  }, []);

  const handleUpdateUser = async (user: User) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${user.id}`, getAdminFetchOptions({
        method: 'PUT',
        body: JSON.stringify({
          email: user.email,
          full_name: user.name,
          role: user.role,
          is_speaker: user.is_speaker,
          status: user.status
        })
      }));

      if (response.ok) {
        setUsers(users.map((u) => (u.id === user.id ? user : u)));
      } else {
        throw new Error('Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, getAdminFetchOptions({
        method: 'DELETE',
      }));

      if (response.ok) {
        setUsers(users.filter((u) => u.id !== userId));
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const handlePromoteToSpeaker = async (userId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/promote`, getAdminFetchOptions({
        method: 'POST',
      }));

      if (response.ok) {
        const user = users.find((u) => u.id === userId);
        if (user) {
          setUsers(users.filter((u) => u.id !== userId));
          const userSpeaker: UserSpeaker = {
            ...user,
            role: 'speaker',
            is_speaker: true
          };
          setSpeakers([...speakers, userSpeaker]);
        }
      }
    } catch (error) {
      console.error('Failed to promote user to speaker:', error);
    }
  };

  const handleCreateSession = async (session: Omit<Session, 'id'>) => {
    try {
      const { speaker, speakers, ...restOfSession } = session;
      const payload = {
        ...restOfSession,
        speakers: speakers?.map(s => ({ id: s.id, display_order: s.display_order })),
        speaker_ids: speakers?.map(s => s.id),
      };

      const response = await fetch(`${API_BASE_URL}/api/admin/sessions`, {
        ...getAdminFetchOptions(),
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create session');
      }

      const newSessionWithUserSpeaker = await response.json();
      const newSession = normalizeSessionFromApi(newSessionWithUserSpeaker);
      setSessions([...sessions, newSession]);

      // If a CFP submission was being scheduled, clear it
      if (schedulingCfp) {
        setSubmissions(submissions.map(sub => 
          sub.id === schedulingCfp.id 
            ? { ...sub, scheduled_time: newSession.startTime, scheduled_room: newSession.room } 
            : sub
        ));
        setSchedulingCfp(null);
      }
    } catch (error) {
      console.error('Error creating session:', error);
    }
  };

  const handleUpdateSession = async (session: Session) => {
    try {
      const { speaker, speakers, ...restOfSession } = session;
      const payload = {
        ...restOfSession,
        speakers: speakers?.map(s => ({ 
          id: s.id, 
          name: s.name,
          display_order: s.display_order 
        })),
        speaker_ids: speakers?.map(s => s.id),
      };

      const response = await fetch(`${API_BASE_URL}/api/admin/sessions/${session.id}`, {
        ...getAdminFetchOptions(),
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update session');
      }

      const updatedSessionWithUserSpeaker = await response.json();
      const updatedSession = normalizeSessionFromApi(updatedSessionWithUserSpeaker);
      setSessions(sessions.map(s => s.id === updatedSession.id ? updatedSession : s));
    } catch (error) {
      console.error('Error updating session:', error);
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/sessions/${sessionId}`, getAdminFetchOptions({
        method: 'DELETE',
      }));

      if (response.ok) {
        setSessions(sessions.filter((s) => s.id !== sessionId));
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  };

  // Admin speaker management handlers (for manual speakers)
  const handleCreateAdminSpeaker = async (speaker: Omit<Speaker, 'id'>) => {
    try {
      // Map frontend Speaker fields to backend expected format
      const speakerCreatePayload = {
        name: speaker.name,
        title: speaker.title,
        bio: speaker.bio,
        company: speaker.company,
        linkedin_url: speaker.linkedin_url,
        display_on_public: speaker.display_on_public,
        bio_short: speaker.bio_short,
        talk_title: speaker.talk_title,
        talk_abstract: speaker.talk_abstract,
      };

      const response = await fetch(`${API_BASE_URL}/api/admin/speakers`, {
        ...getAdminFetchOptions(),
        method: 'POST',
        body: JSON.stringify(speakerCreatePayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create speaker');
      }

      const newSpeaker = await response.json();
      // Map backend response back to frontend Speaker format
      // Re-fetch all speakers to get fresh data with consistent sorting
      const speakersResponse = await fetch(`${API_BASE_URL}/api/admin/speakers`, getAdminFetchOptions());
      if (speakersResponse.ok) {
        const speakersData = await speakersResponse.json();
        const adminSpeakersList = Array.isArray(speakersData) ? speakersData : (speakersData?.speakers || []);
        const mappedAdminSpeakers = adminSpeakersList.map((userSpeaker: any) => mapUserSpeakerToSpeaker(userSpeaker));
        // Sort by speaker ID for consistent ordering
        mappedAdminSpeakers.sort((a: Speaker, b: Speaker) => parseInt(a.id) - parseInt(b.id));
        setAdminSpeakers(mappedAdminSpeakers);
      }
    } catch (error) {
      console.error('Error creating speaker:', error);
    }
  };

  const handleUpdateAdminSpeaker = async (speaker: Speaker) => {
    try {
      // Map frontend Speaker fields to backend expected format
      const speakerUpdatePayload = {
        name: speaker.name,
        title: speaker.title,
        bio: speaker.bio,
        company: speaker.company,
        linkedin_url: speaker.linkedin_url,
        display_on_public: speaker.display_on_public,
        bio_short: speaker.bio_short,
        talk_title: speaker.talk_title,
        talk_abstract: speaker.talk_abstract,
      };

      const response = await fetch(`${API_BASE_URL}/api/admin/speakers/${speaker.id}`, {
        ...getAdminFetchOptions(),
        method: 'PUT',
        body: JSON.stringify(speakerUpdatePayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update speaker');
      }

      // Re-fetch all speakers to get fresh data
      const speakersResponse = await fetch(`${API_BASE_URL}/api/admin/speakers`, getAdminFetchOptions());
      if (speakersResponse.ok) {
        const speakersData = await speakersResponse.json();
        const adminSpeakersList = Array.isArray(speakersData) ? speakersData : (speakersData?.speakers || []);
        const mappedAdminSpeakers = adminSpeakersList.map((userSpeaker: any) => mapUserSpeakerToSpeaker(userSpeaker));
        // Sort by speaker ID for consistent ordering
        mappedAdminSpeakers.sort((a: Speaker, b: Speaker) => parseInt(a.id) - parseInt(b.id));
        setAdminSpeakers(mappedAdminSpeakers);
      }
    } catch (error) {
      console.error('Error updating speaker:', error);
    }
  };

  const handleDeleteAdminSpeaker = async (speakerId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/speakers/${speakerId}`, {
        ...getAdminFetchOptions(),
        method: 'DELETE',
      });

      if (response.ok) {
        setAdminSpeakers(adminSpeakers.filter(s => s.id !== speakerId));
      }
    } catch (error) {
      console.error('Failed to delete speaker:', error);
    }
  };

  const handleImageUpload = async (speakerId: string, file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE_URL}/api/admin/speakers/${speakerId}/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload image');
      }

      const result = await response.json();
      // Update the speaker with the new image filename
      setAdminSpeakers(adminSpeakers.map(s => 
        s.id === speakerId ? { ...s, image: result.filename } : s
      ));
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleAddSponsor = async (sponsor: Omit<Sponsor, 'id'>) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/sponsors`, getAdminFetchOptions({
        method: 'POST',
        body: JSON.stringify(sponsor),
      }));
      if (!response.ok) throw new Error('Failed to add sponsor');
      // Ensure the new sponsor appears immediately by refreshing the list
      const sponsorsRes = await fetch(`${API_BASE_URL}/api/admin/sponsors`, getAdminFetchOptions());
      if (sponsorsRes.ok) {
        const data = await sponsorsRes.json();
        const sponsorsList = Array.isArray(data) ? data : (data?.sponsors || []);
        setSponsors(sponsorsList);
      }
    } catch (error) {
      console.error('Error adding sponsor:', error);
    }
  };

  const handleUpdateSponsor = async (sponsor: Sponsor) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/sponsors/${sponsor.id}`, getAdminFetchOptions({
        method: 'PUT',
        body: JSON.stringify(sponsor),
      }));
      if (!response.ok) throw new Error('Failed to update sponsor');
      setSponsors(sponsors.map((s) => (s.id === sponsor.id ? sponsor : s)));
    } catch (error) {
      console.error('Error updating sponsor:', error);
    }
  };

  const handleDeleteSponsor = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/sponsors/${id}`, getAdminFetchOptions({
        method: 'DELETE',
      }));
      if (!response.ok) throw new Error('Failed to delete sponsor');
      setSponsors(sponsors.filter((s) => s.id !== id));
    } catch (error) {
      console.error('Error deleting sponsor:', error);
    }
  };

  const handleUpdateSpeaker = async (speaker: Speaker) => {
    console.log('handleUpdateSpeaker called with speaker:', { id: speaker.id, name: speaker.name });
    const speakerUpdatePayload = {
      name: speaker.name,
      bio: speaker.bio,
      title: speaker.title || speaker.role,
      company: speaker.company,
      linkedin_url: speaker.linkedin_url || speaker.socialMedia?.linkedin,
      display_on_public: speaker.display_on_public,
      bio_short: speaker.bio_short,
      talk_title: speaker.talk_title,
      talk_abstract: speaker.talk_abstract,
    };

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/speakers/${speaker.id}`,
        getAdminFetchOptions({
          method: 'PUT',
          body: JSON.stringify(speakerUpdatePayload),
        }));
      if (!response.ok) throw new Error('Failed to update speaker');
      const updatedUserSpeaker = await response.json();
      
      // Map backend response back to UserSpeaker format
      const mappedUserSpeaker: UserSpeaker = {
        ...updatedUserSpeaker,
        role: 'speaker' as const,
        is_speaker: true,
        created_at: updatedUserSpeaker.created_at || new Date().toISOString(),
        email: updatedUserSpeaker.email || '',
        status: updatedUserSpeaker.status || 'active',
      };
      
      setSpeakers(speakers.map(s => s.id === mappedUserSpeaker.id ? mappedUserSpeaker : s));
    } catch (error) {
      console.error('Error updating speaker:', error);
    }
  };

  const handleDeleteSpeaker = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/speakers/${id}`,
        getAdminFetchOptions({ method: 'DELETE' }));
      if (!response.ok) throw new Error('Failed to delete speaker');
      setSpeakers(speakers.filter(s => s.id !== id));
    } catch (error) {
      console.error('Error deleting speaker:', error);
    }
  };

  // Removed unused handleAddSpeaker

  const handlePublishNews = async (newsItem: Omit<NewsItem, 'id'>) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/news`, getAdminFetchOptions({
        method: 'POST',
        body: JSON.stringify({ ...newsItem, status: 'published' }),
      }));
      if (!response.ok) throw new Error('Failed to publish news');
      
      // Add the new news item to the state with default values for missing fields
      const responseData = await response.json();
      const newNewsItem: NewsItem = {
        id: responseData.news.id.toString(),
        title: newsItem.title,
        content: newsItem.content,
        publishDate: newsItem.publishDate || new Date().toISOString().split('T')[0],
        author: newsItem.author || 'Conference Team',
        category: newsItem.category || 'announcement',
        featured: newsItem.featured || false,
        status: 'published',
      };
      
      // Correctly update the news state by appending the new item to the existing array
      setNews(prevNews => [...prevNews, newNewsItem]);
    } catch (error) {
      console.error('Error publishing news:', error);
    }
  };

  const handleUpdateNews = async (newsItem: NewsItem) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/news/${newsItem.id}`, getAdminFetchOptions({
        method: 'PUT',
        body: JSON.stringify(newsItem),
      }));
      if (!response.ok) throw new Error('Failed to update news');
      setNews((prevNews) => prevNews.map((n) => (n.id === newsItem.id ? newsItem : n)));
    } catch (error) {
      console.error('Error updating news:', error);
    }
  };

  const handleDeleteNews = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/news/${id}`, getAdminFetchOptions({
        method: 'DELETE',
      }));
      if (!response.ok) throw new Error('Failed to delete news');
      setNews((prevNews) => prevNews.filter((n) => n.id !== id));
    } catch (error) {
      console.error('Error deleting news:', error);
    }
  };

  const handleUpdateSettings = async (settings: any) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/conference-settings`, getAdminFetchOptions({
        method: 'PATCH',
        body: JSON.stringify(settings),
      }));
      if (!response.ok) throw new Error('Failed to update settings');
      setConferenceSettings(await response.json());
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error; // Re-throw to handle in the component
    }
  };

  const handleReviewSubmission = async (submissionId: number, status: string, notes?: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/cfp/submissions/${submissionId}/status`, getAdminFetchOptions({
        method: 'PATCH',
        body: JSON.stringify({ status, notes }),
      }));
      if (!response.ok) throw new Error('Failed to review submission');
      // Refresh submissions
      const updatedSubmissionsRes = await fetch(`${API_BASE_URL}/cfp/submissions`, getAdminFetchOptions());
      const updatedSubmissions = await updatedSubmissionsRes.json();
      setSubmissions(Array.isArray(updatedSubmissions) ? (updatedSubmissions as CfpSubmission[]) : []);
    } catch (error) {
      console.error('Error reviewing submission:', error);
    }
  };

  const handleScheduleSession = (submission: any) => {
    setSchedulingCfp(submission);
    setActiveTab('sessions');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="fixed top-4 right-4">
        <NotificationCenter />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Link to="/" className="text-[#00529B] hover:text-[#003D73] font-medium">
            ← Back to Main Page
          </Link>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-8">Admin Dashboard</h1>

        {/* Stats */}
        <div className="mb-8">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">{users.length}</dd>
                </dl>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Sessions</dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">{totalSessionsCount}</dd>
                </dl>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Registrations</dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">{totalRegistrations}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <nav className="flex flex-wrap gap-2" aria-label="Tabs">
            {[
              { id: 'users', label: 'Users' },
              { id: 'speakers', label: 'Speakers' },
              { id: 'sessions', label: 'Schedule' },
              { id: 'sponsors', label: 'Sponsors' },
              { id: 'news', label: 'News' },
              { id: 'cfp', label: 'Call for Speakers' },
              { id: 'settings', label: 'Settings' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as ActiveTab)}
                className={`${
                  activeTab === tab.id
                    ? 'bg-gray-200 text-gray-800'
                    : 'text-gray-600 hover:text-gray-800'
                } px-3 py-2 font-medium text-sm rounded-md`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="bg-white shadow rounded-lg p-6">
          {activeTab === 'users' && (
            <UserManagement
              users={users} 
              speakers={speakers} 
              onUpdateUser={handleUpdateUser} 
              onDeleteUser={handleDeleteUser} 
              onPromoteToSpeaker={handlePromoteToSpeaker} 
              onUpdateSpeaker={handleUpdateSpeaker} 
              onDeleteSpeaker={handleDeleteSpeaker} 
            />
          )}
          {activeTab === 'speakers' && (
            <SpeakerManagement
              speakers={adminSpeakers}
              onCreateSpeaker={handleCreateAdminSpeaker}
              onUpdateSpeaker={handleUpdateAdminSpeaker}
              onDeleteSpeaker={handleDeleteAdminSpeaker}
              onImageUpload={handleImageUpload}
            />
          )}
          {activeTab === 'sessions' && (
            <SessionManagement
              sessions={sessions}
              speakers={speakerList}
              onCreateSession={handleCreateSession}
              onUpdateSession={handleUpdateSession}
              onDeleteSession={handleDeleteSession}
              prefilledSession={schedulingCfp ? {
                title: schedulingCfp.title,
                description: schedulingCfp.description,
                type: schedulingCfp.type,
                speaker: schedulingCfp.speaker,
                status: 'scheduled'
              } : undefined}
            />
          )}
          {activeTab === 'sponsors' && (
            <SponsorManagement
              sponsors={sponsors}
              onCreateSponsor={handleAddSponsor}
              onUpdateSponsor={handleUpdateSponsor}
              onDeleteSponsor={handleDeleteSponsor}
            />
          )}
          {activeTab === 'news' && (
            <NewsManagement
              news={news}
              onCreateNews={handlePublishNews}
              onUpdateNews={handleUpdateNews}
              onDeleteNews={handleDeleteNews}
            />
          )}
          {activeTab === 'cfp' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Call for Speakers Management</h2>
                <button 
                  onClick={async () => {
                    try {
                      const submissionsRes = await fetch(`${API_BASE_URL}/cfp/submissions`, getAdminFetchOptions());
                      const submissionsData = await submissionsRes.json();
                      setSubmissions(Array.isArray(submissionsData) ? submissionsData : []);
                    } catch (error) {
                      console.error('Error refreshing CFP submissions:', error);
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Refresh Submissions
                </button>
              </div>
              <div className="space-y-6">
                {submissions.length === 0 ? (
                  <p className="text-gray-600">No submissions yet.</p>
                ) : (
                  submissions.map((submission) => (
                    <div key={submission.id} className="border rounded-lg p-6 bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-medium">{submission.title}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            By {submission.speaker_name} from {submission.organization}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          submission.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          submission.status === 'accepted' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                        </span>
                      </div>
                      
                      <div className="mt-4">
                        <h4 className="font-medium">Abstract</h4>
                        <p className="text-gray-600 mt-1 whitespace-pre-wrap">{submission.abstract}</p>
                      </div>
                      
                      <div className="mt-4">
                        <h4 className="font-medium">Speaker Bio</h4>
                        <p className="text-gray-600 mt-1">{submission.speaker_bio}</p>
                      </div>
                      
                      <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium">Email:</span> {submission.email}
                        </div>
                        <div>
                          <span className="font-medium">Presentation Type:</span> {submission.presentation_type}
                        </div>
                      </div>

                      {submission.status === 'pending' && (
                        <div className="mt-6 flex gap-4">
                          <button
                            onClick={() => handleReviewSubmission(submission.id, 'accepted')}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                          >
                            Accept Proposal
                          </button>
                          <button
                            onClick={() => handleReviewSubmission(submission.id, 'rejected')}
                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                          >
                            Reject Proposal
                          </button>
                        </div>
                      )}
                      {submission.status === 'accepted' && !submission.scheduled_time && (
                        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                          <h4 className="font-medium text-blue-800">Action Required</h4>
                          <p className="text-blue-600 mt-1">This accepted talk needs to be scheduled.</p>
                          <button
                            onClick={() => handleScheduleSession(submission)}
                            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                          >
                            Schedule Session
                          </button>
                        </div>
                      )}
                      {submission.status === 'accepted' && submission.scheduled_time && (
                        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
                          <h4 className="font-medium text-green-800">Session Scheduled</h4>
                          <div className="text-green-600 mt-1">
                            <p>Time: {new Date(submission.scheduled_time).toLocaleString()}</p>
                            {submission.scheduled_room && <p>Room: {submission.scheduled_room}</p>}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          {activeTab === 'settings' && (
            <SettingsManagement 
              settings={conferenceSettings} 
              onUpdateSettings={handleUpdateSettings}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
